import { api } from "@/services/api";

export interface ClientPricing {
  id: number;
  clientId: number;
  vendorId: number;
  testTypeId: number;
  price: number;
  currencyCode: string;
  createdAt: string;
  updatedAt: string;
}

export interface ClientPricingRequest {
  clientId: number;
  vendorId: number;
  testTypeId: number;
  price: number;
  currencyCode: string;
}


export const clientPricingApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get pricing by client
    getPricingByClient: builder.query<ClientPricing[], number>({
      query: (clientId) => `/super-admin/clients/${clientId}/pricing`,
      providesTags: ["ClientPricing"],
    }),

    // Get pricing by client and vendor
    getPricingByClientAndVendor: builder.query<
      ClientPricing[],
      { clientId: number; vendorId: number }
    >({
      query: ({ clientId, vendorId }) =>
        `/super-admin/clients/${clientId}/vendors/${vendorId}/pricing`,
      providesTags: ["ClientPricing"],
    }),

    // Create or update pricing
    savePricing: builder.mutation<ClientPricing, ClientPricingRequest>({
      query: (body) => ({
        url: "/super-admin/pricing",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ClientPricing"],
    }),

    // Delete pricing
    deletePricing: builder.mutation<void, number>({
      query: (id) => ({
        url: `/super-admin/pricing/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ClientPricing"],
    }),

    // Calculate basic price (legacy)
    calculatePrice: builder.query<
      PriceCalculationResponse,
      { clientId: number; vendorId: number; clinicId: number; testTypeId: number }
    >({
      query: ({ clientId, vendorId, clinicId, testTypeId }) =>
        `/super-admin/pricing/calculate?clientId=${clientId}&vendorId=${vendorId}&clinicId=${clinicId}&testTypeId=${testTypeId}`,
    }),

    // Calculate detailed price with full breakdown
    calculateDetailedPrice: builder.query<
      DetailedPriceCalculationResponse,
      { clientId: number; vendorId: number; clinicId: number; testTypeId: number }
    >({
      query: ({ clientId, vendorId, clinicId, testTypeId }) =>
        `/super-admin/pricing/calculate-detailed?clientId=${clientId}&vendorId=${vendorId}&clinicId=${clinicId}&testTypeId=${testTypeId}`,
    }),


    // Resolved base prices from clinic_tests (single source of truth)
    // Returns ALL vendors' prices in one call — avoids hooks-in-loop violation
    getResolvedBasePrices: builder.query<BasePriceResponse[], void>({
      query: () => `/super-admin/pricing/base-prices`,
      providesTags: ["Clinics", "Vendors"],
    }),

    // Client Margins
    getClientMargins: builder.query<ClientMargin[], number>({
      query: (clientId) => `/super-admin/client-margins/client/${clientId}`,
      providesTags: ["ClientMargins"],
    }),


    createClientMargin: builder.mutation<ClientMargin, ClientMarginRequest>({
      query: (body) => ({
        url: "/super-admin/client-margins",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ClientMargins"],
    }),

    updateClientMargin: builder.mutation<ClientMargin, { marginId: number; data: ClientMarginRequest }>({
      query: ({ marginId, data }) => ({
        url: `/super-admin/client-margins/${marginId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["ClientMargins"],
    }),

    deleteClientMargin: builder.mutation<void, number>({
      query: (marginId) => ({
        url: `/super-admin/client-margins/${marginId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ClientMargins"],
    }),
  }),
});

export interface PriceCalculationResponse {
    testTypeId: number;
    testName: string;
    testCategory: string;
    basePrice: number;
    marginAmount: number;
    markupPrice: number;
    collectionFee: number;
    totalPrice: number;
    currencyCode: string;
    isCollectionFeeApplied: boolean;
}

export interface DetailedPriceCalculationResponse {
    testTypeId: number;
    testName: string;
    testCategory: string;

    // Step 1: Base Cost
    basePrice: number;
    basePriceSource: string;

    // Step 2: Vendor Margin
    vendorMarginAmount: number;
    vendorMarginType: string;
    vendorMarginValue: number;

    // Step 3: Clinic Margin Override
    clinicMarginAmount: number;
    clinicMarginType: string;
    clinicMarginValue: number;
    clinicMarginApplied: boolean;

    // Step 4: Internal Sell Price
    internalSellPrice: number;

    // Step 5: Collection Fee
    collectionFee: number;
    isCollectionFeeApplied: boolean;

    // Final Results
    finalPrice: number;
    currencyCode: string;
    pricingPriority: string;
}

export interface BasePriceResponse {
  vendorId: number;
  pricingModel: string;
  /** clinicId → (testTypeId → price) */
  clinics: Record<string, Record<string, number>>;
}

export interface ClientMargin {
  id: number;
  clientId: number;
  vendorId?: number;
  vendorName?: string;
  clinicId?: number;
  clinicName?: string;
  testTypeId: number;
  marginType: 'PERCENT' | 'FIXED';
  marginValue: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface ClientMarginRequest {
  clientId: number;
  vendorId?: number;
  clinicId?: number;
  testTypeId: number;
  marginType: 'PERCENT' | 'FIXED';
  marginValue: number;
  isActive?: boolean;
}

export const {
  useGetPricingByClientQuery,
  useGetPricingByClientAndVendorQuery,
  useSavePricingMutation,
  useDeletePricingMutation,
  useCalculatePriceQuery,
  useCalculateDetailedPriceQuery,
  useGetResolvedBasePricesQuery,
  useGetClientMarginsQuery,
  useCreateClientMarginMutation,
  useUpdateClientMarginMutation,
  useDeleteClientMarginMutation,
} = clientPricingApi;
